// Libs
import React from 'react';
import { createSelector } from 'reselect';
import { compose, withHandlers } from '../../../node_module_clones/recompose';
import { connect } from 'react-redux';

// Components
import CanvasContextMenuItems from './CanvasContextMenuItems';

// Selectors
import { getCurrentFocus } from '../../reducers/focusSelector';
import { getClipboardElements } from '../shortcuts/clipboard/clipboardSelectors';
import { getRedoStack, getUndoStack } from '../../utils/undoRedo/undoRedoSelector';
import { getElements } from '../../element/selectors/elementSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { elementGraphSelector } from '../../element/selectors/elementGraphSelector';
import { getGridSize } from '../../utils/grid/gridSizeSelector';
import { getIsSidebarPrototypeEnabled } from '../sidebar/sidebarSelector';

// Actions
import { pasteElements } from '../shortcuts/clipboard/clipboardActions';
import { redoAction, undoAction } from '../../utils/undoRedo/undoRedoActions';
import { selectAllElements } from '../../element/selection/selectionActions';
import { createAndEditElement } from '../../element/actions/elementActions';
import { createTaskList } from '../../element/taskList/taskListActions';
import { createPublicTemplate } from '../../templates/templateActions';

// Utils
import { getCanvasElements, getInboxElements } from '../../../common/elements/utils/elementGraphUtils';

// Constants
import { BoardSections, RootBoardTypes } from '../../../common/boards/boardConstants';
import { DEFAULT_PLACEHOLDER_SIZE } from '../../../common/images/imageConstants';
import { ELEMENT_CREATION_SOURCES } from '../../../common/elements/elementConstants';
import { ElementType } from '../../../common/elements/elementTypes';

const mapStateToProps = createSelector(
    getCurrentFocus,
    getClipboardElements,
    getUndoStack,
    getRedoStack,
    getElements,
    getCurrentBoardId,
    elementGraphSelector,
    getGridSize,
    getIsSidebarPrototypeEnabled,
    (
        currentFocus,
        clipboardElements,
        undoStack,
        redoStack,
        elements,
        currentBoardId,
        elementGraph,
        gridSize,
        isSidebarPrototypeEnabled,
    ) => ({
        currentFocus,
        currentBoardId,

        showPaste: !!clipboardElements.size && currentFocus !== RootBoardTypes.QUICK_NOTES,
        showUndo: !!undoStack.size,
        showRedo: !!redoStack.size,
        showSelectAll:
            currentFocus === BoardSections.INBOX
                ? !!getInboxElements({ elements, elementGraph, parentId: currentBoardId }).size
                : !!getCanvasElements({ elements, elementGraph, parentId: currentBoardId }).size,
        gridSize,
        isSidebarPrototypeEnabled,
    }),
);

const mapDispatchToProps = (dispatch) => ({
    dispatchPasteElements: (location) => dispatch(pasteElements({ location })),
    dispatchUndoAction: () => dispatch(undoAction()),
    dispatchRedoAction: () => dispatch(redoAction()),
    dispatchSelectAll: () => dispatch(selectAllElements()),
    dispatchCreateElement: (args) => dispatch(createAndEditElement(args)),
    dispatchCreateTaskList: (args) => dispatch(createTaskList(args)),
    dispatchCreatePublicTemplate: (args) => dispatch(createPublicTemplate(args)),
});

export const canvasContextMenuConnect = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withHandlers({
        paste:
            ({ dispatchPasteElements, location }) =>
            (event) =>
                dispatchPasteElements(location),
        createNewCard:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.CARD_TYPE,
                    location,
                    currentBoardId,
                    select: true,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewLink:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.LINK_TYPE,
                    location,
                    currentBoardId,
                    select: true,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewTable:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.TABLE_TYPE,
                    location,
                    currentBoardId,
                    select: true,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewLine:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.LINE_TYPE,
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewBoard:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.BOARD_TYPE,
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewFolder:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.BOARD_TYPE,
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                    content: {
                        title: 'New Folder',
                    },
                }),
        createNewColumn:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.COLUMN_TYPE,
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewComment:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.COMMENT_THREAD_TYPE,
                    location,
                    currentBoardId,
                    select: true,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewTaskList:
            ({ dispatchCreateTaskList, currentBoardId, location }) =>
            () =>
                dispatchCreateTaskList({
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                }),
        createNewImageOrFile:
            ({ dispatchCreateElement, currentBoardId, location }) =>
            () =>
                dispatchCreateElement({
                    elementType: ElementType.IMAGE_TYPE,
                    location,
                    currentBoardId,
                    select: false,
                    creationSource: ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
                    // Enforces the aspect ratio of the image placeholder
                    content: {
                        media: DEFAULT_PLACEHOLDER_SIZE,
                    },
                }),
        createNewPublicTemplate:
            ({ dispatchCreatePublicTemplate, currentBoardId, location }) =>
            () =>
                dispatchCreatePublicTemplate({
                    location,
                    currentBoardId,
                }),
    }),
);

const CanvasContextMenuItemsContainer = (props) => <CanvasContextMenuItems {...props} />;

export default canvasContextMenuConnect(CanvasContextMenuItemsContainer);
